<header
  class="h-16 shrink-0 flex items-center justify-between gap-4 px-5 bg-surface border-b border-border relative z-20"
  (ppClickOutside)="closeMenus()"
>
  <!-- Contextual breadcrumb: Module dropdown | Section + favorite -->
  <div class="flex items-center gap-3 min-w-0">
    <div class="relative">
      <button
        type="button"
        (click)="toggleModuleMenu()"
        class="flex items-center gap-1.5 rounded-md px-2 py-1.5 -ml-2 text-section-title text-ink-primary hover:bg-surface-canvas transition-colors"
      >
        <span>{{ activeModule()?.label }}</span>
        <pp-icon name="chevron-down" [size]="16" class="text-ink-muted"></pp-icon>
      </button>

      <!-- Module switcher -->
      <div
        *ngIf="moduleMenuOpen()"
        class="absolute left-0 top-full mt-1.5 min-w-[220px] rounded-lg border border-border bg-surface shadow-flyout py-1.5"
      >
        <div class="px-3 pt-1 pb-1.5 text-label uppercase text-ink-muted">Switch module</div>
        <a
          *ngFor="let module of allModules()"
          [routerLink]="module.children?.[0]?.route ?? module.route"
          (click)="moduleMenuOpen.set(false)"
          class="flex items-center gap-2.5 px-3 py-1.5 mx-1 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-surface-canvas hover:text-ink-primary transition-colors"
          [class.text-brand]="activeModule()?.route === module.route"
          [class.bg-brand-light]="activeModule()?.route === module.route"
        >
          <pp-icon [name]="module.icon" [size]="16"></pp-icon>
          {{ module.label }}
        </a>
      </div>
    </div>

    <span class="w-px h-5 bg-border shrink-0"></span>

    <h1 class="text-section-title text-ink-primary truncate">{{ sectionLabel() }}</h1>

    <button
      type="button"
      (click)="toggleFavorite()"
      class="shrink-0 text-ink-muted hover:text-status-warning transition-colors"
      [class.text-status-warning]="isFavorited()"
      [attr.aria-label]="isFavorited() ? 'Remove from favorites' : 'Add to favorites'"
    >
      <pp-icon name="star" [size]="18" [filled]="isFavorited()"></pp-icon>
    </button>
  </div>

  <!-- Global actions -->
  <div class="flex items-center gap-2 shrink-0">
    <div class="relative hidden md:block">
      <pp-icon
        name="search"
        [size]="16"
        class="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
      ></pp-icon>
      <input
        type="text"
        placeholder="Search vendors, orders, lots…"
        class="w-64 pl-8 pr-3 py-1.5 rounded-md border border-border bg-surface-canvas text-[13px] text-ink-primary
               placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-action/30 focus:border-action
               transition-colors"
      />
    </div>

    <div class="relative">
      <button
        type="button"
        (click)="toggleNotificationsMenu()"
        class="relative w-9 h-9 flex items-center justify-center rounded-md text-ink-secondary hover:bg-surface-canvas transition-colors"
        aria-label="Notifications"
      >
        <pp-icon name="bell" [size]="18"></pp-icon>
        <span *ngIf="unreadCount() > 0" class="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-status-danger"></span>
      </button>

      <div
        *ngIf="notificationsMenuOpen()"
        class="absolute right-0 top-full mt-1.5 w-80 rounded-lg border border-border bg-surface shadow-flyout py-1.5"
      >
        <div class="px-3 pt-1 pb-2 flex items-center justify-between">
          <p class="text-[13px] font-semibold text-ink-primary">Notifications</p>
          <button
            *ngIf="unreadCount() > 0"
            type="button"
            (click)="markAllNotificationsRead()"
            class="text-[12px] font-medium text-action hover:underline"
          >
            Mark all as read
          </button>
        </div>

        <div class="max-h-80 overflow-y-auto">
          <a
            *ngFor="let item of notifications()"
            [routerLink]="item.link"
            (click)="onNotificationClick(item)"
            class="flex items-start gap-2.5 px-3 py-2.5 mx-1 rounded-md hover:bg-surface-canvas transition-colors"
          >
            <span
              class="shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
              [class]="item.read ? 'bg-surface-canvas text-ink-muted' : 'bg-status-danger/10 text-status-danger'"
            >
              <pp-icon [name]="item.icon" [size]="14"></pp-icon>
            </span>
            <div class="min-w-0 flex-1">
              <p class="text-[13px] leading-snug" [class]="item.read ? 'text-ink-secondary' : 'text-ink-primary font-medium'">{{ item.title }}</p>
              <p class="text-[12px] text-ink-muted truncate">{{ item.description }}</p>
              <p class="text-[11px] text-ink-muted mt-0.5">{{ item.date | date: 'MMM d, y' }}</p>
            </div>
            <span *ngIf="!item.read" class="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-action"></span>
          </a>

          <p *ngIf="!notifications().length" class="px-3 py-6 text-center text-[13px] text-ink-muted">
            You're all caught up.
          </p>
        </div>
      </div>
    </div>

    <div class="relative">
      <button
        type="button"
        (click)="toggleUserMenu()"
        class="flex items-center gap-2 rounded-md pl-1 pr-2 py-1 hover:bg-surface-canvas transition-colors"
      >
        <span class="w-7 h-7 rounded-full bg-brand-dark text-white text-[12px] font-semibold flex items-center justify-center">
          {{ userInitials() }}
        </span>
        <pp-icon name="chevron-down" [size]="14" class="text-ink-muted"></pp-icon>
      </button>

      <div
        *ngIf="userMenuOpen()"
        class="absolute right-0 top-full mt-1.5 min-w-[190px] rounded-lg border border-border bg-surface shadow-flyout py-1.5"
      >
        <div class="px-3 pt-1 pb-2 border-b border-border-subtle mb-1">
          <p class="text-[13px] font-medium text-ink-primary">{{ currentUser().name }}</p>
          <p class="text-[12px] text-ink-muted">{{ currentUser().role }}</p>
        </div>
        <a
          routerLink="/account-settings"
          (click)="closeMenus()"
          class="block px-3 py-1.5 mx-1 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-surface-canvas hover:text-ink-primary transition-colors"
        >
          Account settings
        </a>
        <button
          type="button"
          (click)="signOut()"
          class="block w-full text-left px-3 py-1.5 mx-1 rounded-md text-[13px] font-medium text-ink-secondary hover:bg-surface-canvas hover:text-ink-primary transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  </div>
</header>
